import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AppConstants } from 'src/app/app.constants';
import { DecimalPipe } from 'src/_lib/pipes/decimal.pipe';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { CartService } from 'src/_lib/service/cart.service';
import { BrowserService } from 'src/_lib/service/browser.service';

@Component({
  selector: 'app-product-option',
  templateUrl: './product-option.component.html',
  styleUrls: ['./product-option.component.scss'],
  standalone: true,
  imports: [NgFor, NgIf, ReactiveFormsModule, NgxIntlTelInputModule, FormsModule, TitleCasePipe, DecimalPipe]
})
export class ProductOptionComponent implements OnChanges {
  firstObj: number = this.appConstants.firstObj;
  product_key: number;
  isOptionGroup: boolean = true;
  selectedOptionsSkuPrices: number[];
  selectedOptionsSkus: string[];
  selectedOptionsProductVariationKeys: Array<number>;
  selectedOptionsQtys: number[];
  selectedOptionKeys: Array<string> = [];
  numberOfGroups: number;
  groups: { [option_group_key: number]: number | '' } = {};
  groupsDetails: { [option_group_key: number]: any } = {}
  productOptionsDisplay: any[] = [];
  requiredGroup: { [product_option_group_key: number]: number | '' } = {};

  @Input() selectedProductVariationKey: number = null
  @Input() productOptions: any[] = [];
  @Output() sendOptionsDataEvent: EventEmitter<{
    skus: string[],
    qtys: number[],
    prices: number[],
    variationKeys: number[],
    OptionKeys: string[],
    requiredGroup: { [product_option_group_key: number]: number | '' }
  }> = new EventEmitter();
  @Output() productReplacementEvent: EventEmitter<{ variationKey: number, productKey: number, isReplacement: boolean }> = new EventEmitter();

  constructor(
    private appConstants: AppConstants,
    public dialog: MatDialog,
    private cartService: CartService,
    private browser: BrowserService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.productOptions || this.productOptions?.length === 0) return;
    const productKey = this.productOptions?.[0]?.product_option?.[0]?.product_key;
    if (productKey) this.product_key = productKey;
    if (this.browser.isBrowser) {
      const allSaved = JSON.parse(this.browser.localStorage?.getItem('selectedProductOptionsAll') || '{}');
      const savedOptions = allSaved[this.product_key];
      if (savedOptions?.groups && savedOptions?.groupsDetails) {
        this.groups = savedOptions.groups;
        this.groupsDetails = savedOptions.groupsDetails;
      } else {
        this.groups = {};
        this.groupsDetails = {};
      }
    }
    // Always wait until render finishes before emitting data
    this.renderProductOptions(() => {
      this.setSelectedOptions();
      this.sendData();
      this.updateSelectProductSkuFlag();
    });
  }

  sendData() {
    this.sendOptionsDataEvent.emit({
      skus: this.selectedOptionsSkus,
      prices: this.selectedOptionsSkuPrices,
      qtys: this.selectedOptionsQtys,
      variationKeys: this.selectedOptionsProductVariationKeys,
      requiredGroup: this.requiredGroup,
      OptionKeys: this.selectedOptionKeys
    })
  }

  setSelectedOptions() {
    this.selectedOptionsSkus = []
    this.selectedOptionsSkuPrices = [];
    this.selectedOptionsQtys = [];
    this.selectedOptionsProductVariationKeys = []
    this.selectedOptionKeys = [];
    const groupKeys = Object.keys(this.groups);
    for (let i = 0; i < groupKeys?.length; i++) {
      const groupKey = +groupKeys[i];
      const group = this.groupsDetails[groupKey];
      if (group && Object.keys(group)?.length > 0) {
        // SKU
        this.selectedOptionsSkus[i] = group.sku || '';
        // Price
        let price = 0;
        if (group.price_adjustment) {
          price = group.price_adjustment === 'add' ? +group.price
            : group.price_adjustment === 'subtract' ? -Number(group.price)
              : 0;
        }
        this.selectedOptionsSkuPrices[i] = price;
        // Quantity
        this.selectedOptionsQtys[i] = +group.add_to_cart_qty || 0;
        // Variation key
        this.selectedOptionsProductVariationKeys[i] = group.assigned_sku || 0;
        // Option key
        this.selectedOptionKeys[i] = String(group.product_option_key) || '';
      } else {
        this.selectedOptionsSkus[i] = '';
        this.selectedOptionsSkuPrices[i] = 0;
        this.selectedOptionsQtys[i] = 0;
        this.selectedOptionsProductVariationKeys[i] = 0;
        this.selectedOptionKeys[i] = '';
      }
    }
  }

  renderProductOptions(onDone?: () => void) {
    this.productOptionsDisplay = [];
    this.requiredGroup = {};
    this.productOptions.forEach(group => {
      const groupKey = group.product_option_group_key;
      this.groups[groupKey] ??= '';
      this.groupsDetails[groupKey] ??= {};
      // Reset replace_product_sku on refresh
      const prevType = this.groupsDetails[groupKey]?.option_type;
      if (prevType === 'replace_product_sku') {
        this.groups[groupKey] = '';
        this.groupsDetails[groupKey] = {};
      }
      const defaultOption = group.product_option.find(opt => opt.default_selection == 1);
      if (defaultOption && (!this.groups[groupKey] || Object.keys(this.groupsDetails[groupKey])?.length === 0)) {
        this.groupsDetails[groupKey] = defaultOption;
        this.groups[groupKey] = defaultOption.product_option_key;
      } else if (!defaultOption && !this.groups[groupKey]) {
        this.groups[groupKey] = '';
        this.groupsDetails[groupKey] = {};
      }
      if (group.is_required === 1) {
        this.requiredGroup[groupKey] = this.groups[groupKey] || '';
      }
      const filteredOptions = group.product_option.filter(
        opt => opt.assigned_sku != this.selectedProductVariationKey
      );
      if (filteredOptions?.length > 0) {
        this.productOptionsDisplay.push({ ...group, product_option: filteredOptions });
      }
    });
    setTimeout(() => {
      if (onDone) onDone();
    });
  }

  onOptionChange(productOptionKey: number | '', groupKey: number, groupindex: number): void {
    const requiredGroupKeys = Object.keys(this.requiredGroup);
    // Reset this group before applying new selection
    this.groupsDetails[groupKey] = {};
    this.groups[groupKey] = '';
    this.selectedOptionsSkus[groupindex] = '';
    this.selectedOptionsSkuPrices[groupindex] = 0;
    this.selectedOptionsQtys[groupindex] = 0;
    this.selectedOptionsProductVariationKeys[groupindex] = 0;
    this.selectedOptionKeys[groupindex] = '';
    //  If user deselects (blank value)
    if (productOptionKey === '') {
      if (requiredGroupKeys.includes(String(groupKey))) {
        this.requiredGroup[groupKey] = '';
      }
      //  Remove this group from localStorage
      const allSaved = JSON.parse(this.browser.localStorage?.getItem('selectedProductOptionsAll') || '{}');
      const productData = allSaved[this.product_key] || { groups: {}, groupsDetails: {} };
      delete productData.groups[groupKey];
      delete productData.groupsDetails[groupKey];
      allSaved[this.product_key] = productData;
      if (Object.keys(productData.groups)?.length === 0) {
        delete allSaved[this.product_key];
      }
      this.browser.localStorage?.setItem('selectedProductOptionsAll', JSON.stringify(allSaved));
      this.sendData();
      this.updateSelectProductSkuFlag();
      return;
    }
    //  Find the selected option from the group
    const selectedOption = this.productOptionsDisplay
      .find(g => g.product_option_group_key === groupKey)
      ?.product_option.find(opt => +opt.product_option_key === +productOptionKey);
    if (!selectedOption) return;
    this.groupsDetails[groupKey] = selectedOption;
    this.groups[groupKey] = selectedOption.product_option_key;
    if (requiredGroupKeys.includes(String(groupKey))) {
      this.requiredGroup[groupKey] = selectedOption.product_option_key;
    }
    const type = selectedOption.option_type;
    if (type === 'replace_product_sku') {
      // Clear all previously saved option data
      this.browser.localStorage?.removeItem('selectedProductOptionsAll');
      this.groups = {};
      this.groupsDetails = {};
      this.selectedOptionsSkus = [];
      this.selectedOptionsSkuPrices = [];
      this.selectedOptionsQtys = [];
      this.selectedOptionsProductVariationKeys = [];
      this.selectedOptionKeys = [];
      this.requiredGroup = {};
      this.sendProductReplacementData(selectedOption.assigned_sku, selectedOption.product_key);
      return;
    } else if (type === 'enter_option_name') {
      const priceAdj = selectedOption.price_adjustment;
      this.selectedOptionsSkuPrices[groupindex] =
        priceAdj === 'add'
          ? +selectedOption.price
          : priceAdj === 'subtract'
            ? -Number(selectedOption.price)
            : 0;
      this.selectedOptionsQtys[groupindex] = selectedOption.add_to_cart_qty;
      this.selectedOptionsProductVariationKeys[groupindex] = selectedOption.assigned_sku;
      this.selectedOptionKeys[groupindex] = String(selectedOption.product_option_key) || '';
    } else if (type === 'select_product_sku') {
      this.selectedOptionsSkus[groupindex] = selectedOption.sku;
      this.selectedOptionsSkuPrices[groupindex] = +selectedOption.price;
      this.selectedOptionsQtys[groupindex] = selectedOption.add_to_cart_qty;
      this.selectedOptionsProductVariationKeys[groupindex] = selectedOption.assigned_sku;
      this.selectedOptionKeys[groupindex] = String(selectedOption.product_option_key) || '';
    }
    //  Save latest state to localStorage (for ALL option types)
    const allSaved = JSON.parse(this.browser.localStorage?.getItem('selectedProductOptionsAll') || '{}');
    allSaved[this.product_key] = {
      groups: this.groups,
      groupsDetails: this.groupsDetails
    };
    this.browser.localStorage?.setItem('selectedProductOptionsAll', JSON.stringify(allSaved));
    this.sendData();
    this.updateSelectProductSkuFlag();
  }

  updateSelectProductSkuFlag(): void {
    let hasSelectProductSku = false;
    Object.values(this.groupsDetails).forEach(option => {
      if (option && option['option_type'] === 'select_product_sku') {
        hasSelectProductSku = true;
      }
    });
    this.cartService.setSelectProductSkuFlag(hasSelectProductSku);
  }

  sendProductReplacementData(variationKey: number, productKey: number) {
    this.productReplacementEvent.emit({
      variationKey: variationKey,
      productKey: productKey,
      isReplacement: true
    })
  }
}